import axios from 'axios';
import { applyOperationsScope, checkOperationsResponse, isOperationsPath, OPERATIONS_SESSION_INVALID } from './operationsScope';
import { getResolvedApiBaseUrl } from '../utils/environmentRouting';
import {
  AdminInvoiceListResponse,
  AdminInvoiceSummary,
} from '../types/invoice';

export interface MarkInvoicePaidPayload {
  paymentIntentId?: string;
  paymentMethod?: string;
  note?: string;
}

type LocationHostLike = {
  host: string;
};

const BILLING_OPERATION_TIMEOUT_MS = 120000;
const IMPORT_EXECUTION_TIMEOUT_MS = 600000;

// Create axios instance with base configuration
const defaultBaseURL = (() => {
  return getResolvedApiBaseUrl();
})();

export const apiClient = axios.create({
  baseURL: defaultBaseURL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const publicApiClient = axios.create({
  baseURL: defaultBaseURL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

export function getOriginalHostHeaderValue(
  locationOverride?: LocationHostLike | null
): string | undefined {
  const envOverride =
    typeof process !== 'undefined'
      ? process.env.REACT_APP_ORIGINAL_HOST_OVERRIDE
      : undefined;

  const host =
    envOverride ||
    locationOverride?.host ||
    (typeof window !== 'undefined' ? window.location.host : undefined);

  if (typeof host !== 'string' || !host.trim()) {
    return undefined;
  }

  return host.trim().toLowerCase();
}

export function applyOriginalHostHeader<T extends { headers?: unknown }>(
  config: T,
  locationOverride?: LocationHostLike | null
): T {
  const originalHost = getOriginalHostHeaderValue(locationOverride);
  if (!originalHost) {
    return config;
  }

  if (!config.headers) {
    config.headers = {} as T['headers'];
  }

  const headers = config.headers as {
    set?: (name: string, value: string) => void;
  } & Record<string, string>;
  if (typeof headers.set === 'function') {
    headers.set('X-Original-Host', originalHost);
  } else {
    headers['X-Original-Host'] = originalHost;
  }

  return config;
}

export function getRequestPath(url?: string): string {
  if (!url) {
    return '';
  }

  try {
    return new URL(url, 'https://modalhub.local').pathname;
  } catch {
    return url.split('?')[0] || '';
  }
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const [, payload] = token.split('.');
  if (!payload) {
    return null;
  }

  try {
    const base64Payload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const normalizedPayload = base64Payload.padEnd(
      base64Payload.length + ((4 - (base64Payload.length % 4)) % 4),
      '='
    );
    return JSON.parse(atob(normalizedPayload));
  } catch {
    return null;
  }
}

export function isAuthTokenExpiredOrUnreadable(
  token: string | null,
  nowSeconds = Math.floor(Date.now() / 1000)
): boolean {
  if (!token) {
    return false;
  }

  const payload = decodeJwtPayload(token);
  if (!payload) {
    return true;
  }

  return typeof payload.exp === 'number' && payload.exp <= nowSeconds;
}

export function shouldClearSessionForUnauthorized(
  url?: string,
  token?: string | null,
  nowSeconds = Math.floor(Date.now() / 1000)
): boolean {
  if (!token) {
    return false;
  }

  const path = getRequestPath(url);
  const nonSessionClearingPaths = [
    '/auth/login',
    '/auth/register',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/session-view',
    '/quotes/public',
    '/reservation/',
    '/reservations/public',
    '/invoices/public',
    '/payments/requests/public',
    '/customer-portal',
  ];

  if (nonSessionClearingPaths.some((prefix) => path.startsWith(prefix))) {
    return false;
  }

  return isAuthTokenExpiredOrUnreadable(token, nowSeconds);
}

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    applyOriginalHostHeader(config);

    if (isOperationsPath()) return applyOperationsScope(config);

    const token =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem('token')
        : null;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    throw error;
  },
  { synchronous: true }
);

// Response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response) => { checkOperationsResponse(response.config); return response; },
  (error) => {
    if (isOperationsPath() || error.config?.modalOperationsScope) {
      checkOperationsResponse(error.config);
      if ([401, 403].includes(error.response?.status)) {
        window.dispatchEvent(new Event(OPERATIONS_SESSION_INVALID));
      }
      // Query error logging must never include cookie CSRF headers or business payloads.
      return Promise.reject(Object.assign(new Error('Operations request failed. Refresh and check the original receipt.'), {
        response: { status: error.response?.status },
      }));
    }
    const token =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem('token')
        : null;

    if (
      error.response?.status === 401 &&
      typeof localStorage !== 'undefined' &&
      typeof window !== 'undefined' &&
      shouldClearSessionForUnauthorized(error.config?.url, token)
    ) {
      // Token expired or invalid
      localStorage.removeItem('token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

publicApiClient.interceptors.request.use(
  (config) => {
    config.headers.delete('X-Modal-CSRF');
    config.headers.delete('X-Modal-Company-Id');
    config.withCredentials = false;
    return applyOriginalHostHeader(config);
  },
  (error) => Promise.reject(error)
);

// API service functions
export const authAPI = {
  login: (email: string, password: string) =>
    apiClient.post('/auth/login', { email, password }),
  register: (email: string, password: string, confirmPassword: string) =>
    apiClient.post('/auth/register', { email, password, confirmPassword }),
  forgotPassword: (email: string) =>
    apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string, confirmPassword: string) =>
    apiClient.post('/auth/reset-password', {
      token,
      password,
      confirmPassword,
    }),
};

export const bookingAPI = {
  getQuote: (vehicleId: string, startDate: string, endDate: string) =>
    apiClient.get(
      `/booking/quote?vehicleId=${vehicleId}&startDate=${startDate}&endDate=${endDate}`
    ),
  createReservation: (data: {
    vehicleId: string;
    startDate: string;
    endDate: string;
  }) => apiClient.post('/booking/reserve', data),
  getReservations: (params?: {
    status?: string;
    page?: number;
    limit?: number;
  }) => apiClient.get('/booking/reservations', { params }),
  getReservation: (id: string) => apiClient.get(`/booking/reservations/${id}`),
  updateReservation: (id: string, data: any) =>
    apiClient.put(`/booking/reservations/${id}`, data),
  cancelReservation: (id: string) =>
    apiClient.delete(`/booking/reservations/${id}`),
};

export const vehicleAPI = {
  unlockVehicle: (vehicleId: string) =>
    apiClient.post(`/vehicles/${vehicleId}/unlock`),
  immobilizeVehicle: (vehicleId: string) =>
    apiClient.post(`/vehicles/${vehicleId}/immobilize`),
};

export const paymentAPI = {
  getConfig: () => apiClient.get('/payments/config'),
  getPaymentIntent: (paymentIntentId: string) =>
    apiClient.get(`/payments/intent/${paymentIntentId}/status`),
  getPaymentHistory: (limit?: number) =>
    apiClient.get(`/payments/history${limit ? `?limit=${limit}` : ''}`),
  createSetupIntent: () => apiClient.post('/payments/setup-intent'),
  getPaymentProfile: () => apiClient.get('/payments/profile'),
  addPaymentMethod: (paymentMethodId: string) =>
    apiClient.post('/payments/methods', { paymentMethodId }),
  removePaymentMethod: (paymentMethodId: string) =>
    apiClient.delete(`/payments/methods/${paymentMethodId}`),
  setDefaultPaymentMethod: (paymentMethodId: string) =>
    apiClient.put(`/payments/methods/${paymentMethodId}/default`),
};

export const paymentRequestAPI = {
  create: (data: {
    invoiceIds: string[];
    expiresInHours?: number;
    baseUrl?: string;
    purpose?: 'full_balance' | 'deposit';
    depositPercent?: number;
  }) => apiClient.post('/payments/requests', data, {
    timeout: BILLING_OPERATION_TIMEOUT_MS,
  }),
  list: (params?: { status?: string; limit?: number }) =>
    apiClient.get('/payments/requests', { params }),
  get: (requestId: string) => apiClient.get(`/payments/requests/${requestId}`),
  getFeeActivity: (requestId: string) =>
    apiClient.get(`/payments/requests/${requestId}/fee-activity`),
  sendLink: (requestId: string, data?: { baseUrl?: string }) =>
    apiClient.post(`/payments/requests/${requestId}/send-link`, data || {}, {
      timeout: BILLING_OPERATION_TIMEOUT_MS,
    }),
  generateLink: (requestId: string, data?: { baseUrl?: string }) =>
    apiClient.post(`/payments/requests/${requestId}/link`, data || {}, {
      timeout: BILLING_OPERATION_TIMEOUT_MS,
    }),
  markPaid: (
    requestId: string,
    data: { paymentMethod: string; note?: string }
  ) => apiClient.post(`/payments/requests/${requestId}/mark-paid`, data, {
    timeout: BILLING_OPERATION_TIMEOUT_MS,
  }),
  reconcile: (requestId: string) =>
    apiClient.post(`/payments/requests/${requestId}/reconcile`, undefined, {
      timeout: BILLING_OPERATION_TIMEOUT_MS,
    }),
  cancel: (requestId: string, data?: { reason?: string }) =>
    apiClient.put(`/payments/requests/${requestId}/cancel`, data || {}, {
      timeout: BILLING_OPERATION_TIMEOUT_MS,
    }),
  replace: (
    requestId: string,
    data?: { invoiceIds?: string[]; expiresInHours?: number }
  ) => apiClient.post(`/payments/requests/${requestId}/replace`, data || {}, {
    timeout: BILLING_OPERATION_TIMEOUT_MS,
  }),
};

export const publicPaymentRequestAPI = {
  getSummary: (token: string) =>
    publicApiClient.get(
      `/payments/requests/public/${encodeURIComponent(token)}`
    ),
  createPaymentIntent: (token: string) =>
    publicApiClient.post(
      `/payments/requests/public/${encodeURIComponent(token)}/payment-intent`,
      undefined,
      { timeout: BILLING_OPERATION_TIMEOUT_MS }
    ),
  getStatus: (token: string) =>
    publicApiClient.get(
      `/payments/requests/public/${encodeURIComponent(token)}/status`
    ),
  sendSupportContact: (
    token: string,
    data: { name: string; email: string; message: string; publicUrl?: string }
  ) =>
    publicApiClient.post(
      `/payments/requests/public/${encodeURIComponent(token)}/support-contact`,
      data
    ),
  downloadInvoicePdf: (token: string, invoiceId: string, download = true) =>
    publicApiClient.get(
      `/payments/requests/public/${encodeURIComponent(
        token
      )}/invoices/${encodeURIComponent(invoiceId)}/pdf`,
      {
        params: { download: download ? 'true' : 'false' },
        responseType: 'blob',
        timeout: 60000,
      }
    ),
  getBranding: () => publicApiClient.get('/config/branding'),
};

export const publicInvoiceAPI = {
  getInvoice: (token: string) =>
    publicApiClient.get(`/invoices/public/${encodeURIComponent(token)}`),
  downloadInvoicePdf: (token: string, download = true) =>
    publicApiClient.get(`/invoices/public/${encodeURIComponent(token)}/pdf`, {
      params: { download: download ? 'true' : 'false' },
      responseType: 'blob',
      timeout: 60000,
    }),
};

export const customerPortalAPI = {
  startOtp: (email: string) =>
    publicApiClient.post('/customer-portal/otp/start', { email }),
  verifyOtp: (data: { challengeId?: string; code?: string; token?: string }) =>
    publicApiClient.post('/customer-portal/otp/verify', data),
  getMe: (portalToken: string) =>
    publicApiClient.get('/customer-portal/me', {
      headers: { Authorization: `Bearer ${portalToken}` },
    }),
  getRecords: (portalToken: string) =>
    publicApiClient.get('/customer-portal/records', {
      headers: { Authorization: `Bearer ${portalToken}` },
    }),
  acceptQuote: (quoteId: string, portalToken: string) =>
    publicApiClient.post(
      `/customer-portal/quotes/${encodeURIComponent(quoteId)}/accept`,
      {},
      { headers: { Authorization: `Bearer ${portalToken}` } }
    ),
  declineQuote: (quoteId: string, portalToken: string, reason?: string) =>
    publicApiClient.post(
      `/customer-portal/quotes/${encodeURIComponent(quoteId)}/decline`,
      { reason },
      { headers: { Authorization: `Bearer ${portalToken}` } }
    ),
};

async function safeSavedMethodRequest<T>(request: Promise<T>): Promise<T> {
  try { return await request; } catch (failure: any) {
    // React Query logs errors; do not pass Axios headers, bearer URLs or client secrets to it.
    const message = failure?.response?.data?.message || 'Payment method request could not be completed.';
    throw Object.assign(new Error(Array.isArray(message) ? message.join('; ') : String(message)), {
      response: { status: failure?.response?.status, data: { message } },
    });
  }
}
export const savedPaymentMethodsAPI = (context: { customerId?: string; portalToken?: string } = {}) => {
  const client = context.customerId ? apiClient : publicApiClient;
  const path = context.customerId
    ? `/admin/customers/${encodeURIComponent(context.customerId)}/payment-methods`
    : '/customer-portal/payment-methods';
  const config = context.portalToken ? { headers: { Authorization: `Bearer ${context.portalToken}` } } : {};
  return {
    list: () => safeSavedMethodRequest(client.get(path, config)),
    createLink: (methodType: 'card' | 'us_bank_account' = 'card') => safeSavedMethodRequest(client.post(`${path}/setup-links`, { methodType }, config)),
    verification: (id: string) => safeSavedMethodRequest(client.post(`${path}/setups/${encodeURIComponent(id)}/verification`, {}, config)),
    cancel: (id: string) => safeSavedMethodRequest(client.post(`${path}/setups/${encodeURIComponent(id)}/cancel`, {}, config)),
    setDefault: (id: string) => safeSavedMethodRequest(client.put(`${path}/${encodeURIComponent(id)}/default`, {}, config)),
    remove: (id: string) => safeSavedMethodRequest(client.delete(`${path}/${encodeURIComponent(id)}`, config)),
  };
};
export const paymentMethodSetupAPI = {
  summary: (token: string) => safeSavedMethodRequest(publicApiClient.get(`/payment-method-setups/${encodeURIComponent(token)}`)),
  begin: (token: string, data: { consent: boolean; setDefault: boolean; consentVersion: string }) => safeSavedMethodRequest(publicApiClient.post(`/payment-method-setups/${encodeURIComponent(token)}/begin`, data)),
  complete: (token: string) => safeSavedMethodRequest(publicApiClient.post(`/payment-method-setups/${encodeURIComponent(token)}/complete`, {})),
};

export const receiptAPI = {
  getReceipts: (params?: {
    startDate?: string;
    endDate?: string;
    status?: string;
    page?: number;
    limit?: number;
  }) => apiClient.get('/receipts', { params }),
  getReceipt: (id: string) => apiClient.get(`/receipts/${id}`),
  downloadReceipt: (id: string) =>
    apiClient.get(`/receipts/${id}/download`, { responseType: 'blob' }),
  resendInvoiceEmail: (id: string) =>
    apiClient.get(`/receipts/${id}/resend-email`),
};

export const brandingAPI = {
  getBranding: () => apiClient.get('/config/branding'),
};

export const adminAPI = {
  // Dashboard
  getDashboard: () => apiClient.get('/admin/dashboard'),
  getRealtimeMetrics: () => apiClient.get('/admin/metrics/realtime'),

  // Reservations
  getDispatch: (params?: {
    date?: string;
    rangeDays?: number;
  }) => apiClient.get('/admin/dispatch', { params }),
  dispatchReservationAction: (
    id: string,
    data: { action: string; notes?: string | null }
  ) => apiClient.post(`/admin/dispatch/reservations/${id}/actions`, data),
  getAllReservations: (params?: {
    status?: string;
    startDate?: string;
    endDate?: string;
    customerId?: string;
    vehicleId?: string;
    accountId?: string;
    search?: string;
    sourceType?: string;
    assignmentStatus?: string;
    driverAssignmentStatus?: string;
    paymentStatus?: string;
    driverId?: string;
    upcoming?: string;
    page?: number;
    limit?: number;
  }) => apiClient.get('/admin/reservations', { params }),
  createReservation: (data: {
    reservationRef?: string | null;
    vehicleId?: string | null;
    accountId?: string | null;
    customerReference?: string | null;
    customerEmail?: string | null;
    customerName?: string | null;
    customerCompanyName?: string | null;
    passengerName?: string | null;
    startDate?: string;
    endDate?: string;
    totalPrice?: string | null;
    depositAmount?: string | null;
    status?: string;
    statusReason?: string | null;
    sourceType?: string | null;
    pickupTime?: string | null;
    pickupLocation?: string | null;
    dropoffLocation?: string | null;
    affiliate?: string | null;
    vehicleType?: string | null;
    vehicleLabel?: string | null;
    driverName?: string | null;
    paymentMethod?: string | null;
    changeReason?: string | null;
  }) => apiClient.post('/admin/reservations', data),
  getReservation: (id: string) => apiClient.get(`/admin/reservations/${id}`),
  updateReservation: (
    id: string,
    data: {
      reservationRef?: string | null;
      vehicleId?: string | null;
      accountId?: string | null;
      customerReference?: string | null;
      customerEmail?: string | null;
      customerName?: string | null;
      customerCompanyName?: string | null;
      passengerName?: string | null;
      startDate?: string;
      endDate?: string;
      totalPrice?: string | null;
      depositAmount?: string | null;
      status?: string;
      statusReason?: string | null;
      sourceType?: string | null;
      pickupTime?: string | null;
      pickupLocation?: string | null;
      dropoffLocation?: string | null;
      affiliate?: string | null;
      vehicleType?: string | null;
      vehicleLabel?: string | null;
      driverName?: string | null;
      paymentMethod?: string | null;
      changeReason?: string | null;
    }
  ) => apiClient.put(`/admin/reservations/${id}`, data),
  deleteReservation: (id: string) =>
    apiClient.delete(`/admin/reservations/${id}`),
  updateReservationStatus: (
    id: string,
    data: { status: string; reason?: string }
  ) => apiClient.put(`/admin/reservations/${id}/status`, data),
  getAvailableReservationVehicles: (
    id: string,
    params?: { requirementId?: string }
  ) =>
    apiClient.get(`/admin/reservations/${id}/available-vehicles`, { params }),
  createReservationVehicleAssignment: (
    id: string,
    data: {
      quoteRequirementId?: string | null;
      vehicleId: string;
      notes?: string | null;
    }
  ) => apiClient.post(`/admin/reservations/${id}/vehicle-assignments`, data),
  updateReservationVehicleAssignment: (
    id: string,
    assignmentId: string,
    data: {
      status?: string;
      vehicleId?: string;
      notes?: string | null;
    }
  ) =>
    apiClient.put(
      `/admin/reservations/${id}/vehicle-assignments/${assignmentId}`,
      data
    ),
  deleteReservationVehicleAssignment: (id: string, assignmentId: string) =>
    apiClient.delete(
      `/admin/reservations/${id}/vehicle-assignments/${assignmentId}`
    ),
  getAvailableReservationDrivers: (
    id: string,
    params?: { vehicleAssignmentId?: string | null }
  ) =>
    apiClient.get(`/admin/reservations/${id}/available-drivers`, { params }),
  createReservationDriverAssignment: (
    id: string,
    data: {
      driverId: string;
      vehicleAssignmentId?: string | null;
      notes?: string | null;
    }
  ) => apiClient.post(`/admin/reservations/${id}/driver-assignments`, data),
  updateReservationDriverAssignment: (
    id: string,
    assignmentId: string,
    data: {
      status?: string;
      driverId?: string;
      vehicleAssignmentId?: string | null;
      notes?: string | null;
    }
  ) =>
    apiClient.put(
      `/admin/reservations/${id}/driver-assignments/${assignmentId}`,
      data
    ),
  deleteReservationDriverAssignment: (id: string, assignmentId: string) =>
    apiClient.delete(
      `/admin/reservations/${id}/driver-assignments/${assignmentId}`
    ),
  generateReservationPublicRecordLink: (
    id: string,
    data?: { baseUrl?: string }
  ) =>
    apiClient.post(`/admin/reservations/${id}/public-record-link`, data || {}),
  revokeReservationPublicRecordLink: (id: string) =>
    apiClient.post(`/admin/reservations/${id}/revoke-public-record-link`),
  createReservationBalanceInvoice: (
    id: string,
    data?: { dueDate?: string; memo?: string | null }
  ) => apiClient.post(`/admin/reservations/${id}/balance-invoice`, data || {}),
  createReservationDepositInvoice: (
    id: string,
    data?: { baseUrl?: string }
  ) => apiClient.post(`/admin/reservations/${id}/deposit-invoice`, data || {}),
  attachReservationInvoice: (reservationId: string, invoiceId: string) =>
    apiClient.put(`/admin/reservations/${reservationId}/invoices/${invoiceId}`),
  detachReservationInvoice: (reservationId: string, invoiceId: string) =>
    apiClient.delete(
      `/admin/reservations/${reservationId}/invoices/${invoiceId}`
    ),
  bulkReservationOperation: (data: {
    operation: string;
    reservationIds: string[];
    reason?: string;
  }) => apiClient.post('/admin/reservations/bulk', data),

  // Quotes
  getQuotes: (params?: {
    status?: string;
    accountId?: string;
    fromDate?: string;
    toDate?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => apiClient.get('/admin/quotes', { params }),
  createQuote: (data: any) => apiClient.post('/admin/quotes', data),
  getQuote: (quoteId: string) => apiClient.get(`/admin/quotes/${quoteId}`),
  updateQuote: (quoteId: string, data: any) =>
    apiClient.put(`/admin/quotes/${quoteId}`, data),
  recalculateQuote: (quoteId: string) =>
    apiClient.post(`/admin/quotes/${quoteId}/recalculate`),
  generateQuoteLink: (quoteId: string, data?: { baseUrl?: string }) =>
    apiClient.post(`/admin/quotes/${quoteId}/link`, data || {}),
  revokeQuoteLink: (quoteId: string) =>
    apiClient.post(`/admin/quotes/${quoteId}/revoke-link`),
  sendQuote: (quoteId: string, data?: { baseUrl?: string }) =>
    apiClient.post(`/admin/quotes/${quoteId}/send`, data || {}),
  acceptQuote: (quoteId: string, data?: { baseUrl?: string }) =>
    apiClient.post(`/admin/quotes/${quoteId}/accept`, data || {}),
  declineQuote: (quoteId: string, data?: { reason?: string }) =>
    apiClient.post(`/admin/quotes/${quoteId}/decline`, data || {}),
  convertQuoteToReservation: (
    quoteId: string,
    data?: { requireAccepted?: boolean }
  ) =>
    apiClient.post(`/admin/quotes/${quoteId}/convert-reservation`, data || {}),
  updateAccountCommercialTerms: (
    accountId: string,
    commercialTerms: Record<string, any> | null
  ) =>
    apiClient.put(`/admin/quotes/account-terms/${accountId}`, {
      commercialTerms,
    }),

  // Customers
  getCustomers: (params?: {
    search?: string;
    kycStatus?: string;
    loyaltyTier?: string;
    page?: number;
    limit?: number;
  }) => apiClient.get('/admin/customers', { params }),
  getCustomer: (id: string) => apiClient.get(`/admin/customers/${id}`),
  updateCustomerKyc: (id: string, data: { status: string; reason?: string }) =>
    apiClient.put(`/admin/customers/${id}/kyc-status`, data),
  getCustomerLTV: (id: string) =>
    apiClient.get(`/admin/customers/${id}/lifetime-value`),
  logCommunication: (
    id: string,
    data: {
      type: string;
      subject: string;
      content: string;
      outcome: string;
    }
  ) => apiClient.post(`/admin/customers/${id}/communications`, data),
  getCommunications: (id: string) =>
    apiClient.get(`/admin/customers/${id}/communications`),

  // Accounts
  getAccounts: (params?: { search?: string; page?: number; limit?: number }) =>
    apiClient.get('/admin/accounts', { params }),
  getAccount: (accountId: string) =>
    apiClient.get(`/admin/accounts/${accountId}`),
  getAccountTerms: (accountId: string) =>
    apiClient.get(`/admin/accounts/${accountId}/terms`),
  updateAccountTerms: (
    accountId: string,
    commercialTerms: Record<string, any> | null
  ) => apiClient.put(`/admin/accounts/${accountId}/terms`, { commercialTerms }),
  createAccount: (data: { name: string }) =>
    apiClient.post('/admin/accounts', data),
  backfillAccounts: () => apiClient.post('/admin/accounts/backfill', {}),
  updateAccount: (accountId: string, data: { name: string }) =>
    apiClient.put(`/admin/accounts/${accountId}`, data),
  deleteAccount: (accountId: string) =>
    apiClient.delete(`/admin/accounts/${accountId}`),
  assignCustomerAccountByIdentity: (data: {
    accountId: string | null;
    email: string;
    firstName?: string | null;
    lastName?: string | null;
    phone?: string | null;
    stripeCustomerId?: string | null;
  }) => apiClient.post('/admin/accounts/assign-customer', data),
  getAccountCustomers: (params?: {
    search?: string;
    page?: number;
    limit?: number;
    accountId?: string;
  }) => apiClient.get('/admin/accounts/customers', { params }),
  getSingleAccountCustomers: (accountId: string) =>
    apiClient.get(`/admin/accounts/${accountId}/customers`),
  getAccountInvoices: (accountId: string) =>
    apiClient.get(`/admin/accounts/${accountId}/invoices`),
  attachCustomerToAccount: (accountId: string, customerId: string) =>
    apiClient.put(`/admin/accounts/${accountId}/customers/${customerId}`),
  detachCustomerFromAccount: (accountId: string, customerId: string) =>
    apiClient.delete(`/admin/accounts/${accountId}/customers/${customerId}`),
  setAccountPrimaryBillingContact: (accountId: string, customerId: string) =>
    apiClient.put(`/admin/accounts/${accountId}/primary-billing-contact`, { customerId }),
  addAccountContact: (accountId: string, data: { customerId: string; makePrimary?: boolean }) =>
    apiClient.post(`/admin/accounts/${accountId}/contacts`, data),
  updateAccountContact: (
    accountId: string,
    contactId: string,
    data: { role?: string; isPrimaryBilling?: boolean; status?: string }
  ) => apiClient.put(`/admin/accounts/${accountId}/contacts/${contactId}`, data),

  // Analytics
  getReservationAnalytics: (params?: {
    startDate?: string;
    endDate?: string;
  }) => apiClient.get('/admin/analytics/reservations', { params }),
  getRevenueAnalytics: (params?: { startDate?: string; endDate?: string }) =>
    apiClient.get('/admin/analytics/revenue', { params }),
  getOperationalAnalytics: (params?: {
    startDate?: string;
    endDate?: string;
  }) => apiClient.get('/admin/analytics/operational', { params }),
  getBusinessIntelligence: (params?: {
    startDate?: string;
    endDate?: string;
  }) => apiClient.get('/admin/analytics/intelligence', { params }),
  generateReport: (data: {
    reportType: string;
    parameters?: Record<string, any>;
    startDate?: string;
    endDate?: string;
  }) => apiClient.post('/admin/reports/generate', data),

  // Billing Management
  getAllInvoices: (params?: {
    search?: string;
    status?: string;
    customerScope?: string;
    customerId?: string;
    dueDate?: string;
    invoiceType?: string;
    startDate?: string;
    endDate?: string;
    minAmount?: string;
    maxAmount?: string;
    page?: number;
    limit?: number;
  }) =>
    apiClient.get<AdminInvoiceListResponse>('/invoices/admin/all', { params }),

  getBillingOverview: (params?: {
    status?: string;
    customerId?: string;
    dueDate?: string;
    invoiceType?: string;
    minAmount?: string;
    maxAmount?: string;
  }) => apiClient.get<AdminInvoiceSummary>('/invoices/admin/stats', { params }),

  generateInvoice: (data: {
    customerId: string;
    reservationId?: string;
    items: Array<{
      description: string;
      quantity: number;
      unitPrice: number;
    }>;
    dueDate?: string;
    notes?: string;
  }) =>
    apiClient.post('/invoices/admin/custom', {
      customer_id: data.customerId,
      reservation_id: data.reservationId,
      line_items: data.items.map((item) => ({
        description: item.description,
        quantity: item.quantity,
        unit_amount: Math.round(item.unitPrice * 100), // Convert to cents
      })),
      due_date: data.dueDate,
      memo: data.notes,
    }),

  sendInvoice: (invoiceId: string) =>
    apiClient.post(`/invoices/${invoiceId}/send`),

  markInvoicePaid: (invoiceId: string, data: MarkInvoicePaidPayload = {}) =>
    apiClient.put(`/invoices/admin/${invoiceId}/mark-paid`, data),

  finalizeInvoice: (invoiceId: string) =>
    apiClient.put(`/invoices/admin/${invoiceId}/finalize`, {}, { timeout: BILLING_OPERATION_TIMEOUT_MS }),

  getInvoice: (invoiceId: string) => apiClient.get(`/invoices/${invoiceId}`),

  getInvoicePublicLink: (invoiceId: string) =>
    apiClient.post(`/invoices/${invoiceId}/public-link`),

  getInvoicePaymentActivity: (invoiceId: string) =>
    apiClient.get(`/invoices/admin/${invoiceId}/payment-activity`),
  getInvoiceSyncState: (invoiceId: string) =>
    apiClient.get(`/invoices/admin/${invoiceId}/sync-state`),
  retryInvoiceSync: (invoiceId: string) =>
    apiClient.post(`/invoices/admin/${invoiceId}/retry-sync`, undefined, { timeout: BILLING_OPERATION_TIMEOUT_MS }),

  updateInvoice: (invoiceId: string, data: any) =>
    apiClient.put(`/invoices/${invoiceId}`, data),

  voidInvoice: (invoiceId: string, reason?: string) =>
    apiClient.put(`/invoices/admin/${invoiceId}/void`, { reason }),

  deleteInvoice: (invoiceId: string) =>
    apiClient.delete(`/invoices/admin/${invoiceId}`),

  // Disputes
  getDisputes: (params?: { status?: string; page?: number; limit?: number }) =>
    apiClient.get('/admin/disputes', { params }),

  // Stripe Connect
  getStripeConnectStatus: () => apiClient.get('/admin/stripe-connect/status'),
  getStripeConnectHealth: () => apiClient.get('/admin/stripe-connect/health'),
  getStripeConnectOAuthLink: (redirectPath?: string) =>
    apiClient.get('/admin/stripe-connect/oauth-link', {
      params: redirectPath ? { redirectPath } : undefined,
    }),
  testStripeConnection: () => apiClient.post('/admin/stripe-connect/test'),
  refreshStripeConnectStatus: () =>
    apiClient.post('/admin/stripe-connect/refresh'),

  // General Settings
  getGeneralSettings: () => apiClient.get('/admin/settings/general'),
  getPlatformFeePolicy: () => apiClient.get('/admin/settings/platform-fee'),
  getQuoteTerms: () => apiClient.get('/admin/settings/quote-terms'),
  updateQuoteTerms: (quoteTerms: Record<string, any>) =>
    apiClient.put('/admin/settings/quote-terms', { quoteTerms }),
  getEventTerms: () => apiClient.get('/admin/settings/event-terms'),
  updateEventTerms: (eventTerms: Record<string, any>) =>
    apiClient.put('/admin/settings/event-terms', { eventTerms }),

  updateGeneralSettings: (settings: {
    companyName: string;
    companyAddress: string;
    taxId?: string;
    supportEmail: string;
    supportPhone?: string;
    invoicePrefix: string;
    timezone: string;
  }) => apiClient.put('/admin/settings/general', settings),

  // Branding
  updateBranding: (branding: {
    logo_url?: string;
    app_name?: string;
    primary_color?: string;
    secondary_color?: string;
    favicon_url?: string;
  }) => apiClient.put('/admin/settings/branding', branding),

  // Onboarding
  getOnboardingStatus: () => apiClient.get('/admin/onboarding/status'),
  completeOnboarding: () => apiClient.put('/admin/onboarding/complete'),
  dismissOnboarding: () => apiClient.put('/admin/onboarding/dismiss'),

  // Vehicle Management
  getAllVehicles: (params?: {
    search?: string;
    status?: string;
    location?: string;
    type?: string;
    page?: number;
    limit?: number;
  }) => apiClient.get('/admin/vehicles', { params }),

  createVehicle: (vehicleData: {
    make: string;
    model: string;
    year: number;
    fleetNumber?: string;
    licensePlate: string;
    vin?: string | null;
    location: string;
    hourlyRate: string;
    dailyRate: string;
    features: string[];
    type: string;
    vehicleType: string;
    color?: string;
    capacity?: number;
  }) => {
    // Map frontend field names to backend field names
    const payload = {
      make: vehicleData.make,
      model: vehicleData.model,
      year: vehicleData.year,
      fleet_number: vehicleData.fleetNumber,
      license_plate: vehicleData.licensePlate,
      vin: vehicleData.vin?.trim() || null,
      color: vehicleData.color,
      vehicle_type: vehicleData.vehicleType,
      passenger_capacity: vehicleData.capacity,
      hourly_rate: parseFloat(vehicleData.hourlyRate),
      current_mileage: 0,
      status: 'available',
    };
    return apiClient.post('/admin/vehicles', payload);
  },

  updateVehicle: (vehicleId: string, vehicleData: any) => {
    const payload = {
      make: vehicleData.make,
      model: vehicleData.model,
      year: vehicleData.year,
      fleet_number: vehicleData.fleetNumber ?? vehicleData.fleet_number,
      license_plate: vehicleData.licensePlate ?? vehicleData.license_plate,
      vin:
        typeof vehicleData.vin === 'string'
          ? vehicleData.vin.trim() || null
          : vehicleData.vin,
      color: vehicleData.color,
      vehicle_type: vehicleData.vehicleType ?? vehicleData.vehicle_type,
      passenger_capacity:
        vehicleData.capacity ?? vehicleData.passenger_capacity,
      hourly_rate:
        vehicleData.hourlyRate != null
          ? parseFloat(vehicleData.hourlyRate)
          : vehicleData.hourly_rate,
      current_mileage:
        vehicleData.currentMileage ?? vehicleData.current_mileage,
      status: vehicleData.status,
    };
    return apiClient.patch(`/admin/vehicles/${vehicleId}`, payload);
  },

  deleteVehicle: (vehicleId: string) =>
    apiClient.delete(`/admin/vehicles/${vehicleId}`),

  getVehicle: (vehicleId: string) =>
    apiClient.get(`/admin/vehicles/${vehicleId}`),

  // Customer Management
  createCustomer: (customerData: {
    email: string;
    firstName: string;
    lastName: string;
    phone?: string;
    companyName?: string;
    accountRelationship?: {
      mode: 'none' | 'link_existing' | 'create_new';
      accountId?: string | null;
      accountName?: string | null;
      makePrimaryBillingContact?: boolean;
    };
  }) => apiClient.post('/admin/customers', customerData),

  updateCustomer: (customerId: string, customerData: any) =>
    apiClient.put(`/admin/customers/${customerId}/profile`, customerData),

  // CSV Import
  downloadCustomerImportTemplate: () =>
    apiClient.get('/admin/customers/import-template', { responseType: 'blob' }),

  startCustomerImportBatch: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/admin/customers/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },

  getCustomerImportBatchStatus: (batchId: string) =>
    apiClient.get(`/admin/customers/import/batches/${batchId}/status`),

  resyncCustomers: () => apiClient.post('/admin/customers/resync'),

  // Driver Management
  getAllDrivers: (params?: {
    search?: string;
    status?: string;
    employmentType?: string;
    qualification?: string;
    page?: number;
    limit?: number;
  }) => apiClient.get('/admin/drivers', { params }),

  getDriver: (driverId: string) => apiClient.get(`/admin/drivers/${driverId}`),

  createDriver: (driverData: {
    firstName: string;
    lastName: string;
    email?: string | null;
    phone?: string | null;
    licenseNumber?: string | null;
    licenseExpiry?: string | null;
    medicalCardExpiry?: string | null;
    backgroundCheckExpiry?: string | null;
    employmentType: 'full_time' | 'part_time' | 'contractor';
    status?: string;
    hourlyRate?: string;
    homeBase?: string | null;
    vehicleTypeQualifications?: string[];
    notes?: string | null;
    weeklyAvailability?: Array<{
      dayOfWeek: number;
      startTime: string;
      endTime: string;
      active?: boolean;
    }>;
    emergencyContact?: {
      name?: string;
      phone?: string;
      relationship?: string;
    } | null;
  }) => apiClient.post('/admin/drivers', driverData),

  updateDriver: (driverId: string, driverData: any) =>
    apiClient.put(`/admin/drivers/${driverId}`, driverData),

  deleteDriver: (driverId: string) =>
    apiClient.delete(`/admin/drivers/${driverId}`),

  getDriverSchedule: (driverId: string, params?: { from?: string; to?: string }) =>
    apiClient.get(`/admin/drivers/${driverId}/schedule`, { params }),

  createDriverScheduleBlock: (
    driverId: string,
    data: {
      startAt: string;
      endAt: string;
      blockType: string;
      notes?: string | null;
    }
  ) => apiClient.post(`/admin/drivers/${driverId}/schedule-blocks`, data),

  updateDriverScheduleBlock: (
    driverId: string,
    blockId: string,
    data: {
      startAt?: string;
      endAt?: string;
      blockType?: string;
      notes?: string | null;
    }
  ) => apiClient.put(`/admin/drivers/${driverId}/schedule-blocks/${blockId}`, data),

  deleteDriverScheduleBlock: (driverId: string, blockId: string) =>
    apiClient.delete(`/admin/drivers/${driverId}/schedule-blocks/${blockId}`),

  // Logo Upload
  uploadLogo: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/admin/upload/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Invoice Attachments
  uploadInvoiceAttachment: (invoiceId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/invoices/${invoiceId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteInvoiceAttachment: (invoiceId: string, attachmentId: string) =>
    apiClient.delete(`/invoices/${invoiceId}/attachments/${attachmentId}`),

  // Access Requests (company admin)
  getAccessRequests: () => apiClient.get('/admin/access-requests'),
  approveAccessRequest: (id: string) =>
    apiClient.put(`/admin/access-requests/${id}/approve`),
  denyAccessRequest: (id: string, reason?: string) =>
    apiClient.put(`/admin/access-requests/${id}/deny`, { reason }),

  // Bulk Invoice Import
  uploadInvoiceCsv: (
    file: File,
    options?: { payment_terms?: string; tax_rate?: number }
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    const params: Record<string, string> = {};
    if (options?.payment_terms) params.payment_terms = options.payment_terms;
    if (options?.tax_rate != null) params.tax_rate = String(options.tax_rate);
    return apiClient.post('/invoices/import/csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params,
      timeout: 120000,
    });
  },

  uploadInvoicePdf: (
    file: File,
    options?: {
      payment_terms?: string;
      tax_rate?: number;
      method?: 'programmatic' | 'ai';
    }
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    const params: Record<string, string> = {};
    if (options?.payment_terms) params.payment_terms = options.payment_terms;
    if (options?.tax_rate != null) params.tax_rate = String(options.tax_rate);
    if (options?.method === 'ai') params.method = 'ai';
    return apiClient.post('/invoices/import/pdf', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params,
      timeout: options?.method === 'ai' ? 600000 : 120000,
    });
  },

  getImportBatches: (params?: {
    status?: string;
    page?: number;
    limit?: number;
  }) => apiClient.get('/invoices/import/batches', { params }),

  getImportBatch: (batchId: string) =>
    apiClient.get(`/invoices/import/batches/${batchId}`),

  getImportBatchPreview: (batchId: string) =>
    apiClient.get(`/invoices/import/batches/${batchId}/preview`),

  approveImportBatch: (
    batchId: string,
    adjustments?: Array<{
      row_number: number;
      matched_customer_id?: string;
      invoice_group?: string;
      exclude?: boolean;
      contact_reconciliation_action?:
        | 'use_for_invoice_only'
        | 'add_as_account_billing_contact'
        | 'set_as_primary_billing_contact'
        | 'update_matched_customer_contact';
    }>
  ) =>
    apiClient.put(`/invoices/import/batches/${batchId}/approve`, {
      adjustments,
    }),

  executeImportBatch: (batchId: string) =>
    apiClient.post(`/invoices/import/batches/${batchId}/execute`, undefined, {
      timeout: IMPORT_EXECUTION_TIMEOUT_MS,
    }),

  cancelImportBatch: (batchId: string) =>
    apiClient.put(`/invoices/import/batches/${batchId}/cancel`),

  deleteImportBatch: (batchId: string) =>
    apiClient.delete(`/invoices/import/batches/${batchId}`),

  downloadInvoiceImportTemplate: () =>
    apiClient.get('/invoices/import/csv-template', { responseType: 'blob' }),
};

export const publicQuoteAPI = {
  getQuote: (token: string) =>
    publicApiClient.get(`/quotes/public/${encodeURIComponent(token)}`),
  acceptQuote: (token: string, data?: { baseUrl?: string }) =>
    publicApiClient.post(
      `/quotes/public/${encodeURIComponent(token)}/accept`,
      data || {}
    ),
  declineQuote: (token: string, data?: { reason?: string }) =>
    publicApiClient.post(
      `/quotes/public/${encodeURIComponent(token)}/decline`,
      data || {}
    ),
};

export const publicReservationAPI = {
  getReservationRecord: (token: string) =>
    publicApiClient.get(`/reservations/public/${encodeURIComponent(token)}`),
};

export const platformAPI = {
  // Users
  createUser: (data: { email: string; password: string }) =>
    apiClient.post('/platform/users', data),
  getUsers: (params?: { search?: string; companyId?: string; role?: string }) =>
    apiClient.get('/platform/users', { params }),
  updateUser: (
    id: string,
    data: { email?: string; role?: string; kyc_status?: string }
  ) => apiClient.patch(`/platform/users/${id}`, data),
  resetUserPassword: (id: string) =>
    apiClient.put(`/platform/users/${id}/reset-password`),

  // Support staff
  getSupportStaff: () => apiClient.get('/platform/support-staff'),
  getSupportWritePermissionCatalog: () =>
    apiClient.get('/platform/support-staff/permissions'),
  createSupportStaff: (data: {
    email: string;
    password: string;
    accessible_company_ids: string[];
    support_write_permissions?: string[];
  }) => apiClient.post('/platform/support-staff', data),
  updateSupportStaffCompanies: (
    id: string,
    data: { accessible_company_ids: string[] }
  ) => apiClient.patch(`/platform/support-staff/${id}/companies`, data),
  updateSupportStaffWritePermissions: (
    id: string,
    data: { support_write_permissions: string[] }
  ) => apiClient.patch(`/platform/support-staff/${id}/write-permissions`, data),
  setSupportStaffPassword: (
    id: string,
    data: { password: string; confirmPassword?: string }
  ) => apiClient.patch(`/platform/support-staff/${id}/password`, data),
  removeSupportStaff: (id: string) =>
    apiClient.delete(`/platform/support-staff/${id}`),
  getAccessibleTenantCompanies: () =>
    apiClient.get('/platform/tenant-access/companies'),
  getSupportTenantSwitchCompanies: () =>
    apiClient.get('/support/tenant-switch/companies'),

  // Companies
  getCompanies: () => apiClient.get('/platform/companies'),
  getCompanyCustomerProfile: (
    companyId: string,
    params: { customerId?: string; email?: string }
  ) =>
    apiClient.get(`/platform/companies/${companyId}/customers/profile`, {
      params,
    }),
  createCompany: (data: any) => apiClient.post('/platform/companies', data),
  updateCompany: (id: string, data: any) =>
    apiClient.patch(`/platform/companies/${id}`, data),
  getCompanyPlatformFeePolicy: (id: string) =>
    apiClient.get(`/platform/companies/${id}/platform-fee-policy`),
  updateCompanyPlatformFeePolicy: (
    id: string,
    data: {
      mode: 'inherit' | 'custom';
      ratePercent?: number | string | null;
      effectiveFrom?: string | null;
      agreementReference?: string | null;
      reason?: string | null;
    },
  ) => apiClient.put(`/platform/companies/${id}/platform-fee-policy`, data),
  uploadCompanyLogo: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/platform/companies/${id}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  suspendCompany: (id: string) =>
    apiClient.put(`/platform/companies/${id}/suspend`),
  activateCompany: (id: string) =>
    apiClient.put(`/platform/companies/${id}/activate`),
  softDeleteCompany: (id: string) =>
    apiClient.delete(`/platform/companies/${id}`),
  reconcileDeleteCompany: (id: string, data?: { notes?: string }) =>
    apiClient.put(`/platform/companies/${id}/reconcile-delete`, data || {}),
  permanentDeleteCompany: (id: string) =>
    apiClient.delete(`/platform/companies/${id}/permanent`, {
      params: { confirm: 'true' },
    }),

  // Access Requests (platform admin)
  requestCompanyAccess: (companyId: string) =>
    apiClient.post(`/platform/companies/${companyId}/access-request`),
  getAccessRequests: () => apiClient.get('/platform/access-requests'),

  // Invoices
  getInvoices: (params?: {
    status?: string;
    companyId?: string;
    page?: number;
    limit?: number;
  }) => apiClient.get('/platform/invoices', { params }),
  getInvoiceStats: () => apiClient.get('/platform/invoices/stats'),

  // Billing operations
  getBillingHealth: () => apiClient.get('/platform/billing-operations/health'),
  getStripeAccountHealth: (params?: {
    companyId?: string;
    status?: string;
    limit?: number;
    page?: number;
  }) => apiClient.get('/platform/billing-operations/stripe-account-health', { params }),
  getStripeAccountHealthSweeps: (params?: { limit?: number; page?: number }) =>
    apiClient.get('/platform/billing-operations/stripe-account-health/sweeps', { params }),
  runStripeAccountHealthSweep: () =>
    apiClient.post('/platform/billing-operations/stripe-account-health/sweep'),
  getBillingSummary: (params?: {
    companyId?: string;
    staleMinutes?: number;
    maxAttempts?: number;
  }) => apiClient.get('/platform/billing-operations/summary', { params }),
  getBillingAlerts: (params?: {
    limit?: number;
    page?: number;
    target?: string;
    severity?: string;
    alertType?: string;
    deliveryStatus?: string;
    workflowStatus?: string;
    startDate?: string;
    endDate?: string;
  }) => apiClient.get('/platform/billing-operations/alerts', { params }),
  exportBillingAlerts: (params?: {
    limit?: number;
    target?: string;
    severity?: string;
    alertType?: string;
    deliveryStatus?: string;
    workflowStatus?: string;
    startDate?: string;
    endDate?: string;
  }) =>
    apiClient.get('/platform/billing-operations/alerts/export', {
      params,
      responseType: 'blob',
      headers: { Accept: 'text/csv' },
    }),
  getBillingAlert: (id: string) =>
    apiClient.get(`/platform/billing-operations/alerts/${id}`),
  acknowledgeBillingAlert: (id: string, note?: string) =>
    apiClient.post(
      `/platform/billing-operations/alerts/${id}/acknowledge`,
      note ? { note } : {}
    ),
  handoffBillingAlert: (
    id: string,
    data: { provider: string; reference?: string; note?: string }
  ) =>
    apiClient.post(`/platform/billing-operations/alerts/${id}/handoff`, data),
  resolveBillingAlert: (id: string, note: string) =>
    apiClient.post(`/platform/billing-operations/alerts/${id}/resolve`, {
      note,
    }),
  retryBillingAlert: (id: string) =>
    apiClient.post(`/platform/billing-operations/alerts/${id}/retry`),
  retryFailedBillingAlerts: (params?: {
    target?: 'webhook_events' | 'invoice_sync_jobs';
    limit?: number;
  }) =>
    apiClient.post('/platform/billing-operations/alerts/retry-failed', null, {
      params,
    }),
  getWebhookEvents: (params?: {
    status?: string;
    eventType?: string;
    stripeAccountId?: string;
    companyId?: string;
    limit?: number;
    page?: number;
    startDate?: string;
    endDate?: string;
  }) =>
    apiClient.get('/platform/billing-operations/webhook-events', { params }),
  exportWebhookEvents: (params?: {
    status?: string;
    eventType?: string;
    stripeAccountId?: string;
    companyId?: string;
    limit?: number;
    startDate?: string;
    endDate?: string;
  }) =>
    apiClient.get('/platform/billing-operations/webhook-events/export', {
      params,
      responseType: 'blob',
      headers: { Accept: 'text/csv' },
    }),
  replayWebhookEvent: (id: string) =>
    apiClient.post(`/platform/billing-operations/webhook-events/${id}/replay`),
  replayWebhookEventsBulk: (ids: string[]) =>
    apiClient.post('/platform/billing-operations/webhook-events/replay-bulk', {
      ids,
    }),
  getInvoiceSyncJobs: (params?: {
    status?: string;
    companyId?: string;
    invoiceId?: string;
    limit?: number;
    page?: number;
    startDate?: string;
    endDate?: string;
  }) =>
    apiClient.get('/platform/billing-operations/invoice-sync-jobs', { params }),
  exportInvoiceSyncJobs: (params?: {
    status?: string;
    companyId?: string;
    invoiceId?: string;
    limit?: number;
    startDate?: string;
    endDate?: string;
  }) =>
    apiClient.get('/platform/billing-operations/invoice-sync-jobs/export', {
      params,
      responseType: 'blob',
      headers: { Accept: 'text/csv' },
    }),
  retryInvoiceSyncJob: (id: string) =>
    apiClient.post(
      `/platform/billing-operations/invoice-sync-jobs/${id}/retry`
    ),
  retryInvoiceSyncJobsBulk: (ids: string[]) =>
    apiClient.post(
      '/platform/billing-operations/invoice-sync-jobs/retry-bulk',
      { ids }
    ),
  getBillingReconciliationSummary: (params?: {
    companyId?: string;
    limit?: number;
    staleMinutes?: number;
    maxAttempts?: number;
  }) =>
    apiClient.get('/platform/billing-operations/reconciliation/summary', {
      params,
    }),
  recoverWebhookEvents: (params?: {
    limit?: number;
    maxAttempts?: number;
    staleMinutes?: number;
  }) =>
    apiClient.post(
      '/platform/billing-operations/webhook-events/recover',
      null,
      { params }
    ),
  recoverInvoiceSyncJobs: (params?: {
    limit?: number;
    maxAttempts?: number;
    staleMinutes?: number;
  }) =>
    apiClient.post(
      '/platform/billing-operations/invoice-sync-jobs/recover',
      null,
      { params }
    ),
  resyncOpenInvoices: (params?: { companyId?: string; limit?: number }) =>
    apiClient.post(
      '/platform/billing-operations/reconciliation/resync-open-invoices',
      null,
      { params }
    ),
  repairStripeStateDrift: (params?: { companyId?: string; limit?: number }) =>
    apiClient.post(
      '/platform/billing-operations/reconciliation/repair-stripe-state',
      null,
      { params }
    ),
  repairCustomerMappings: (params?: { companyId?: string; limit?: number }) =>
    apiClient.post(
      '/platform/billing-operations/reconciliation/repair-customer-mappings',
      null,
      { params }
    ),
  repairFeeConsistency: (params?: { companyId?: string; limit?: number }) =>
    apiClient.post(
      '/platform/billing-operations/reconciliation/repair-fee-consistency',
      null,
      { params }
    ),
  repairHistoricalPaidFees: (params?: {
    companyId?: string;
    limit?: number;
    batchSize?: number;
  }) =>
    apiClient.post(
      '/platform/billing-operations/reconciliation/repair-historical-paid-fees',
      null,
      { params }
    ),
  repairDisputeConsistency: (params?: { companyId?: string; limit?: number }) =>
    apiClient.post(
      '/platform/billing-operations/reconciliation/repair-dispute-consistency',
      null,
      { params }
    ),
  reviewDisputeCandidate: (
    id: string,
    data: { workflowStatus: 'reviewed' | 'resolved'; note?: string },
    params?: { companyId?: string }
  ) =>
    apiClient.post(
      `/platform/billing-operations/reconciliation/dispute-candidates/${id}/review`,
      data,
      { params }
    ),
};
